import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiUrl } from "../config/api";
import { clearClientSession, getAuthHeaders } from "../config/auth";
import {
  AppShell,
  ButtonBase,
  CardPanel,
  DataTable,
  FilterBar,
  PanelTitleRow,
  PaginationBar,
  TopbarActionGroup,
} from "../components";

const ManageQuestions = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [studentName, setStudentName] = useState("");
  const [appliedStudentName, setAppliedStudentName] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [modalContent, setModalContent] = useState(null);
  const [replyMessage, setReplyMessage] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [teacherName, setTeacherName] = useState("");
  const navigate = useNavigate();

  const handleLogout = () => {
    clearClientSession();
    navigate("/");
  };

  const fetchQuestions = useCallback(async () => {
    setLoading(true);

    try {
      const query = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });

      if (appliedStudentName.trim()) {
        query.append("studentName", appliedStudentName.trim());
      }

      const response = await fetch(
        `${apiUrl("/api/questions/all")}?${query.toString()}`,
        {
          headers: {
            ...getAuthHeaders(),
          },
        },
      );

      const result = await response.json();
      setQuestions(result.data || []);
      setTotalPages(result.totalPages || 1);
    } catch (err) {
      console.error("Error fetching questions:", err);
      setQuestions([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [appliedStudentName, limit, page]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  const handleSearch = () => {
    setPage(1);
    setAppliedStudentName(studentName);
  };

  const handleClear = () => {
    setStudentName("");
    setAppliedStudentName("");
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const openModal = (question) => {
    setModalContent(question);
  };

  const closeModal = () => {
    setModalContent(null);
  };

  const columns = useMemo(
    () => [
      {
        key: "name",
        label: "Student Name",
      },
      {
        key: "email",
        label: "Student Email",
        className: "ui-data-table__muted",
      },
      {
        key: "question",
        label: "Question",
        className: "ui-data-table__muted",
        render: (entry) => (
          <span
            title={entry.question}
            style={{
              display: "inline-block",
              maxWidth: "280px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {entry.question}
          </span>
        ),
      },
      {
        key: "timestamp",
        label: "Time and Date",
        className: "ui-data-table__muted",
        render: (entry) => new Date(entry.timestamp).toLocaleString(),
      },
      {
        key: "actions",
        label: "Action",
        mobileLabel: "Action",
        render: (entry) => (
          <ButtonBase
            variant="danger"
            size="sm"
            className="ui-action-btn"
            onClick={() => openModal(entry)}
          >
            Reply
          </ButtonBase>
        ),
      },
    ],
    [],
  );

  const handleReply = async () => {
    if (!modalContent || !replyMessage.trim()) {
      return;
    }

    setSendingReply(true);

    try {
      const response = await fetch(apiUrl("/api/email/reply"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          questionId: modalContent.id,
          to: modalContent.email,
          studentName: modalContent.name,
          question: modalContent.question,
          reply: replyMessage,
          teacherName: teacherName.trim() || "Sunday School Team",
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to send reply.");
      }

      const savedReply = result.reply;

      setReplyMessage("");

      if (savedReply) {
        setModalContent((current) =>
          current
            ? {
                ...current,
                replies: [...(current.replies || []), savedReply],
              }
            : current,
        );

        setQuestions((currentQuestions) =>
          currentQuestions.map((item) =>
            item.id === modalContent.id
              ? {
                  ...item,
                  replies: [...(item.replies || []), savedReply],
                }
              : item,
          ),
        );
      }

      alert("Reply sent successfully.");
    } catch (error) {
      console.error("Error sending reply:", error);
      alert(error.message || "Failed to send reply.");
    } finally {
      setSendingReply(false);
    }
  };
  return (
    <AppShell
      logoPosition="bottom"
      mainClassName="ui-shell__main--admin"
      topbarActions={
        <TopbarActionGroup
          showDashboard
          onDashboard={() => navigate("/HomePage")}
          onLogout={handleLogout}
        />
      }
    >
      <CardPanel className="ui-card-panel--wide ui-admin-panel">
        <div className="ui-admin-card">
          <PanelTitleRow title="Manage Questions" />

          <div className="ui-admin-card__top">
            <div className="ui-table-search">
              <FilterBar
                fields={[{ name: "studentName", placeholder: "Student Name" }]}
                values={{ studentName }}
                onFieldChange={(event) => setStudentName(event.target.value)}
                onSubmit={handleSearch}
                onClear={handleClear}
                submitLabel="Search"
                clearLabel="Clear"
                submitVariant="navy"
                columns={1}
                className="ui-questions-filter"
              />
            </div>
          </div>

          <DataTable
            columns={columns}
            data={questions}
            keyField={(row, rowIndex) =>
              `${row.id || row.timestamp}-${row.email}-${rowIndex}`
            }
            loading={loading}
            loadingText="Loading questions..."
            emptyText="No questions found."
          />

          <PaginationBar
            page={page}
            totalPages={totalPages}
            limit={limit}
            onPageChange={handlePageChange}
            onLimitChange={(newLimit) => {
              setLimit(newLimit);
              setPage(1);
            }}
          />
        </div>
      </CardPanel>

      {modalContent ? (
        <div className="ui-modal-overlay">
          <div
            className="ui-modal"
            style={{
              maxHeight: "85vh",
              overflowY: "auto",
            }}
          >
            <h2 className="ui-modal__title">Student Question</h2>
            <p className="ui-modal__row">
              <strong>Name:</strong> {modalContent.name}
            </p>
            <p className="ui-modal__row">
              <strong>Email:</strong> {modalContent.email}
            </p>
            <p className="ui-modal__row">
              <strong>Question:</strong> {modalContent.question}
            </p>
            <p className="ui-modal__row">
              <strong>Date:</strong>{" "}
              {new Date(modalContent.timestamp).toLocaleString()}
            </p>
            <div className="ui-modal__row">
              <label htmlFor="teacherName">
                <strong>Teacher replying:</strong>
              </label>
              <input
                id="teacherName"
                value={teacherName}
                onChange={(event) => setTeacherName(event.target.value)}
                placeholder="e.g. Teacher Dare"
                style={{ width: "100%", marginTop: "8px", padding: "12px" }}
              />
            </div>
            {modalContent.replies?.length ? (
              <div className="ui-modal__row">
                <strong>Previous Replies:</strong>

                <div
                  style={{
                    marginTop: "10px",
                    display: "grid",
                    gap: "10px",
                    maxHeight: "220px",
                    overflowY: "auto",
                    paddingRight: "4px",
                  }}
                >
                  {modalContent.replies.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        padding: "12px",
                        border: "1px solid #d6d9e6",
                        borderRadius: "10px",
                        background: "#f8f9fc",
                      }}
                    >
                      <p style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                        {item.reply}
                      </p>

                      <small style={{ color: "#6b7280" }}>
                        Replied by {item.teacherName || "Sunday School Team"} •
                        Sent to {item.sentTo} on{" "}
                        {new Date(item.sentAt).toLocaleString()}
                      </small>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="ui-modal__row">
                <strong>Previous Replies:</strong> No replies yet.
              </p>
            )}

            <div className="ui-modal__row">
              <label htmlFor="replyMessage">
                <strong>Reply:</strong>
              </label>
              <textarea
                id="replyMessage"
                value={replyMessage}
                onChange={(event) => setReplyMessage(event.target.value)}
                placeholder="Type your reply here..."
                rows={6}
                style={{ width: "100%", marginTop: "8px", padding: "12px" }}
              />
            </div>
            <div className="ui-modal__actions">
              <ButtonBase variant="utility" onClick={closeModal}>
                Close
              </ButtonBase>
              <ButtonBase
                variant="navy"
                onClick={handleReply}
                disabled={sendingReply}
              >
                {sendingReply ? "Sending..." : "Send Reply"}
              </ButtonBase>
            </div>
          </div>
        </div>
      ) : null}
    </AppShell>
  );
};

export default ManageQuestions;
